import { useState, useEffect } from 'react';
import type { TreeNode } from '../hooks/useTree';
import { listFoldersCached } from '../hooks/useDrive';

interface Props {
  show: boolean;
  itemCount: number;
  roots: TreeNode[];
  expanded: Set<string>;
  currentPath?: string;
  onToggle: (path: string) => void;
  onConfirm: (destinationPath: string) => void;
  onCancel: () => void;
}

function TreeNodeItem({ 
  node, 
  selectedPath, 
  expanded, 
  onToggle, 
  onSelect, 
  depth,
  onLoadChildren
}: { 
  node: TreeNode; 
  selectedPath: string; 
  expanded: Set<string>; 
  onToggle: (path: string) => void; 
  onSelect: (path: string) => void; 
  depth: number;
  onLoadChildren: (path: string) => void;
}) {
  const isExpanded = expanded.has(node.path);
  const isSelected = selectedPath === node.path;
  const hasChildren = !node.loaded || (node.children && node.children.length > 0);

  const handleToggle = () => {
    if (!node.loaded) {
      onLoadChildren(node.path);
    }
    onToggle(node.path);
  };

  return (
    <li className="list-unstyled mb-0">
      <div
        className={`d-flex align-items-center px-2 py-1 rounded tree-node ${isSelected ? 'active' : ''}`}
        style={{ cursor: 'pointer', paddingLeft: `${depth * 20 + 8}px`, fontSize: '13px', userSelect: 'none' }}
        onClick={() => onSelect(node.path)}
        onDoubleClick={() => handleToggle()}
      >
        <span
          className="me-1"
          style={{ width: 16, flexShrink: 0, fontSize: 10, opacity: hasChildren ? 1 : 0 }}
          onClick={e => { e.stopPropagation(); handleToggle(); }}
        >
          {isExpanded ? '▼' : '▶'}
        </span>
        <i className={`bi ${isExpanded ? 'bi-folder2-open' : 'bi-folder'} me-1`} style={{ fontSize: 14, color: '#f0ad4e' }} />
        <span className="text-truncate">{node.name}</span>
      </div>

      {isExpanded && node.children && node.children.length > 0 && (
        <ul className="list-unstyled mb-0" style={{ marginLeft: 16 }}>
          {node.children.map(child => (
            <TreeNodeItem
              key={child.path}
              node={child}
              selectedPath={selectedPath}
              expanded={expanded}
              onToggle={onToggle}
              onSelect={onSelect}
              depth={depth + 1}
              onLoadChildren={onLoadChildren}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

export default function MoveModal({ show, itemCount, roots: initialRoots, expanded: initialExpanded, currentPath = '', onToggle, onConfirm, onCancel }: Props) {
  const [selectedPath, setSelectedPath] = useState<string>(currentPath);
  const [roots, setRoots] = useState<TreeNode[]>(initialRoots);
  const [expanded, setExpanded] = useState<Set<string>>(initialExpanded);
  const [loading, setLoading] = useState<Set<string>>(new Set());

  // Sincronizar roots cuando cambian los iniciales (tree.roots se actualiza)
  useEffect(() => {
    if (initialRoots.length > 0) {
      setRoots(prev => (prev.length === 0 ? initialRoots : prev));
    }
  }, [initialRoots]);

  useEffect(() => {
    if (!show) return;

    setSelectedPath(currentPath);

    // Solo cargar roots si no llegaron en props (o están vacíos)
    if (initialRoots.length === 0) {
      loadRootFolders();
    }

    // Auto-expandir el árbol hasta el path actual
    if (currentPath) {
      const pathSegments = currentPath.split('/').filter(Boolean);
      const pathsToExpand: string[] = [];
      let accumulatedPath = '';
      for (const segment of pathSegments) {
        accumulatedPath = accumulatedPath ? `${accumulatedPath}/${segment}` : segment;
        pathsToExpand.push(accumulatedPath);
      }

      setExpanded(prev => {
        const next = new Set(prev);
        pathsToExpand.forEach(p => next.add(p));
        return next;
      });

      // Cargar hijos en paralelo (la caché evita llamadas duplicadas)
      Promise.all(pathsToExpand.map(p => loadChildren(p))).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, currentPath]);

  const loadRootFolders = async () => {
    try {
      const folders = await listFoldersCached('');
      const rootNodes: TreeNode[] = folders.map(f => ({
        name: f.name,
        path: f.path.replace(/\/$/, ''),
        loaded: false,
        children: [],
      }));
      setRoots(rootNodes);
    } catch (e) {
      console.error('Failed to load root folders:', e);
    }
  };

  const loadChildren = async (path: string) => {
    if (loading.has(path)) return;

    // Si ese nodo ya está cargado en el árbol actual, no volver a fetchear
    if (isNodeLoaded(roots, path)) return;

    setLoading(prev => new Set(prev).add(path));

    try {
      const folders = await listFoldersCached(path);
      const children: TreeNode[] = folders.map(f => ({
        name: f.name,
        path: f.path.replace(/\/$/, ''),
        loaded: false,
        children: [],
      }));

      setRoots(prev => updateNodeChildren(prev, path, children));
    } catch (e) {
      console.error('Failed to load children:', e);
    } finally {
      setLoading(prev => {
        const next = new Set(prev);
        next.delete(path);
        return next;
      });
    }
  };

  const isNodeLoaded = (nodes: TreeNode[], targetPath: string): boolean => {
    for (const n of nodes) {
      if (n.path === targetPath) return n.loaded === true;
      if (n.children && n.children.length > 0) {
        const found = isNodeLoaded(n.children, targetPath);
        if (found) return true;
      }
    }
    return false;
  };

  const updateNodeChildren = (nodes: TreeNode[], targetPath: string, children: TreeNode[]): TreeNode[] => {
    return nodes.map(node => {
      if (node.path === targetPath) {
        return { ...node, loaded: true, children };
      }
      if (node.children && node.children.length > 0) {
        return { ...node, children: updateNodeChildren(node.children, targetPath, children) };
      }
      return node;
    });
  };

  const handleToggle = (path: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
    onToggle(path);
  };

  if (!show) return null;

  return (
    <div className="modal show d-block feebbo-modal" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} onClick={onCancel}>
      <div className="modal-dialog modal-dialog-centered" onClick={e => e.stopPropagation()}>
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Mover {itemCount} {itemCount === 1 ? 'elemento' : 'elementos'}</h5>
            <button type="button" className="btn-close" onClick={onCancel} />
          </div>
          <div className="modal-body">
            <p className="small text-body-secondary mb-3">Selecciona la carpeta de destino:</p>
            <div 
              className="border rounded p-2" 
              style={{ maxHeight: 400, overflowY: 'auto', background: '#f8fafc' }}
            >
              {/* Root */}
              <div
                className={`d-flex align-items-center px-2 py-1 mb-1 tree-node ${selectedPath === '' ? 'active' : ''}`}
                style={{ cursor: 'pointer', fontSize: '13px', userSelect: 'none' }}
                onClick={() => setSelectedPath('')}
              >
                <i className="bi bi-house-door me-2" style={{ fontSize: 14 }} />
                <span>Mi unidad</span>
              </div>

              {/* Tree */}
              <ul className="list-unstyled mb-0">
                {roots.map(node => (
                  <TreeNodeItem
                    key={node.path}
                    node={node}
                    selectedPath={selectedPath}
                    expanded={expanded}
                    onToggle={handleToggle}
                    onSelect={setSelectedPath}
                    depth={0}
                    onLoadChildren={loadChildren}
                  />
                ))}
              </ul>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-feebbo-outline" onClick={onCancel}>
              Cancelar
            </button>
            <button 
              type="button" 
              className="btn btn-feebbo" 
              onClick={() => onConfirm(selectedPath)}
            >
              Mover aquí
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
